import { ReactNode, useEffect } from "react";
import config from "@/config";
import { routes } from "@/router";
import { useLogto } from "@logto/react";
import posthog from "posthog-js";
import { useNavigate } from "react-router-dom";

import { useGetOrgaInfo } from "@/hooks/api/organisation/useGetOrgaInfo";
import { useGetUser } from "@/hooks/api/user/useGetUser";
import PageLoader from "@/components/molecules/PageLoader";

type AuthGuardProps = {
  children: ReactNode;
  isProtected?: boolean;
};

const AuthGuard = ({ children, isProtected = true }: AuthGuardProps) => {
  const { signIn, isAuthenticated, isLoading } = useLogto();
  const navigate = useNavigate();

  const { data: userData, isLoading: isUserLoading } = useGetUser();
  const { data: orgaData, isLoading: isOrgaLoading } = useGetOrgaInfo();

  useEffect(() => {
    if (!isLoading && !isAuthenticated && isProtected) {
      signIn(config.FRONTEND_URL + config.LOGTO_CALLBACK);
    }
  }, [isAuthenticated, isLoading, isProtected]);

  useEffect(() => {
    if (userData && !posthog._isIdentified()) {
      posthog.identify(userData.id, {
        email: userData.email,
      });
    }
  }, [userData]);

  // Redirect to onboarding if user or organisation onboarding is not finished
  useEffect(() => {
    if (
      !isUserLoading &&
      !isOrgaLoading &&
      (userData?.onboarding_step !== "FINISHED" || orgaData?.onboarding_step !== "FINISHED")
    ) {
      navigate(routes.onboarding);
    }
  }, [userData, orgaData, isAuthenticated, isUserLoading, isOrgaLoading]);

  useEffect(() => {
    if (
      userData?.onboarding_step === "FINISHED" &&
      orgaData?.onboarding_step === "FINISHED" &&
      window.location.pathname === routes.onboarding
    ) {
      navigate(routes.search);
    }
  }, [userData, orgaData, isAuthenticated]);

  if (isProtected && (isLoading || !isAuthenticated || isUserLoading || isOrgaLoading)) {
    return <PageLoader />;
  }

  return <>{children}</>;
};

export default AuthGuard;
